import { useEffect, useState } from 'react';
import { Layout, Menu, Button, Dropdown, Tag, Space, Select, type MenuProps } from 'antd';
import {
  GlobalOutlined,
  UserOutlined,
  LogoutOutlined,
  MoonOutlined,
  SunOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useLogout } from '@digiwedge/hooks-auth-web';
import { useGlobalStore } from '@digiwedge/global-state';
import { useTheme } from '@digiwedge/ui-theme';
import { useI18n } from '../i18n.js';
import { useEnvLabel } from '../util/useEnvLabel.js';
import { apiGet } from '../api.js';
import RouteProgress from '../ui/RouteProgress.js';
import { CmpThemeProvider } from '@digiwedge/cmp-consent-react';
import { useThemeTokens } from '../../lib/api/useThemeTokens.js';
import { FLAGS } from '../../config/flags.js';
import { useAuth } from '../../lib/auth/useAuth.js';
import { canReadConfig } from '../../lib/auth/permissions.js';
import { DocsLinkButton } from '@digiwedge/ui-docs-link';
import { DOCS_CONFIG } from '../../lib/help-links.js';

const { Header, Sider, Content } = Layout;

export default function AppShell() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { logout } = useLogout();
  const token = useGlobalStore((s) => s.token);
  const { mode, toggleTheme } = useTheme();
  const { language, setLanguage, direction } = useI18n();
  const env = useEnvLabel();
  const { scopes } = useAuth();
  const showThemeNav = FLAGS.THEME_EDITOR && canReadConfig(scopes);

  const envMeta = import.meta.env as unknown as {
    VITE_REGISTRY_URL?: string;
    VITE_CDN_CMP_URL?: string;
  };
  const registryUrl = envMeta?.VITE_REGISTRY_URL ?? 'http://localhost:3410';
  const cdnUrl = envMeta?.VITE_CDN_CMP_URL ?? '';

  const [siteOptions, setSiteOptions] = useState<string[]>([]);
  const [siteKey, setSiteKey] = useState<string | undefined>(
    () => localStorage.getItem('cmp.portal.lastSiteKey') || undefined,
  );
  const themeState = useThemeTokens(registryUrl, siteKey ?? null);
  type SiteOption = { label: string; value: string };
  const [siteOpts, setSiteOpts] = useState<SiteOption[]>([]);
  const [siteSearch, setSiteSearch] = useState<string>('');
  const [loadingSites, setLoadingSites] = useState(false);

  useEffect(() => {
    const loadSites = async () => {
      try {
        const rows = await apiGet<Array<{ siteKey: string }>>(`${registryUrl}/admin/sites`);
        const keys = rows.map((r) => r.siteKey);
        setSiteOptions(keys);
        if (!siteKey && keys.length > 0) setSiteKey(keys[0]);
      } catch {
        setSiteOptions([]);
      }
    };
    loadSites().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryUrl]);

  // Persist last protected route (for post-login redirects across reloads)
  useEffect(() => {
    try {
      localStorage.setItem('cmp.portal.lastRoute', `${pathname}${search}`);
    } catch {
      /* ignore */
    }
  }, [pathname, search]);

  // Debounced remote search for Site Select
  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoadingSites(true);
      try {
        const url = new URL('/admin/sites', registryUrl);
        if (siteSearch) url.searchParams.set('q', siteSearch);
        url.searchParams.set('page', '1');
        url.searchParams.set('pageSize', '20');
        const resp = await apiGet<{ items?: { key: string; name: string }[] }>(url.toString());
        const opts = (resp.items ?? []).map((s) => ({ label: s.name, value: s.key }));
        if (!cancelled) setSiteOpts(opts);
        if (!cancelled && siteKey && !(resp.items ?? []).some((s) => s.key === siteKey)) {
          setSiteOpts((prev) => [{ label: siteKey, value: siteKey }, ...prev]);
        }
      } catch {
        if (!cancelled) setSiteOpts([]);
      } finally {
        if (!cancelled) setLoadingSites(false);
      }
    }, 250);
    return () => {
      window.clearTimeout(timer);
      cancelled = true;
    };
  }, [registryUrl, siteSearch, siteKey]);

  const userMenu = {
    items: [
      {
        key: 'lang-en',
        label: 'English',
        icon: <GlobalOutlined />,
        onClick: () => setLanguage('en'),
      },
      {
        key: 'lang-ar',
        label: 'العربية',
        icon: <GlobalOutlined />,
        onClick: () => setLanguage('ar'),
      },
      { type: 'divider' as const },
      {
        key: 'logout',
        danger: true,
        icon: <LogoutOutlined />,
        label: 'Logout',
        onClick: () => logout(),
      },
    ],
  };

  const selectedKey = (() => {
    if (pathname.startsWith('/config')) return '/config';
    if (pathname.startsWith('/admin/themes')) return '/admin/themes';
    if (pathname.startsWith('/export')) return '/export';
    if (pathname.startsWith('/analytics')) return '/analytics';
    if (pathname.startsWith('/overrides')) return '/overrides';
    if (pathname.startsWith('/scans')) return '/scans';
    return pathname;
  })();

  const menuItems: MenuProps['items'] = [
    { key: '/sites', label: 'Sites' },
    { key: '/analytics', label: 'Analytics' },
    { key: '/overrides', label: 'Overrides' },
    { key: '/scans', label: 'Scans' },
    { key: '/export', label: 'Exports' },
    { key: '/config', label: 'Config Editor' },
    { key: '/diagnostics', label: 'Diagnostics' },
    { key: '/health', label: 'Health' },
  ];
  if (showThemeNav) {
    menuItems.splice(5, 0, { key: '/admin/themes', label: 'Themes' });
  }

  return (
    <CmpThemeProvider theme={themeState.tokens}>
      <Layout style={{ minHeight: '100vh' }} dir={direction}>
        <Sider breakpoint="lg" collapsedWidth={64}>
          <div style={{ padding: 16, color: '#fff', fontWeight: 600 }}>CMP</div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            onClick={(e) => navigate(String(e.key))}
            items={menuItems}
          />
        </Sider>

        <Layout>
          <Header style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Space style={{ flex: 1 }}>
              <Tag color={env.color}>{env.label}</Tag>
              <Select
                aria-label="Select site"
                placeholder="Select site"
                style={{ minWidth: 240 }}
                value={siteKey}
                onChange={(v) => {
                  setSiteKey(v);
                  try {
                    localStorage.setItem('cmp.portal.lastSiteKey', v);
                  } catch {
                    /* ignore */
                  }
                }}
                onSearch={(value) => setSiteSearch(value)}
                showSearch
                filterOption={false}
                loading={loadingSites}
                allowClear
                options={
                  siteOpts.length ? siteOpts : siteOptions.map((k) => ({ value: k, label: k }))
                }
              />
            </Space>
            <Space>
              <DocsLinkButton
                pathname={pathname}
                config={DOCS_CONFIG}
                icon={<QuestionCircleOutlined />}
              />
              <Button
                type="text"
                aria-label="Toggle theme"
                icon={mode === 'dark' ? <SunOutlined /> : <MoonOutlined />}
                onClick={toggleTheme}
              />
              <Dropdown menu={userMenu} placement="bottomRight" trigger={['click']}>
                <Button type="text" icon={<UserOutlined />}>
                  {token ? 'Account' : 'Guest'} · {language.toUpperCase()}
                </Button>
              </Dropdown>
            </Space>
          </Header>
          <Content style={{ padding: 20 }}>
            <RouteProgress />
            <Outlet context={{ registryUrl, cdnUrl, siteKey, setSiteKey }} />
          </Content>
        </Layout>
      </Layout>
    </CmpThemeProvider>
  );
}
